import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  asapScheduler,
  distinctUntilChanged,
  map,
  merge,
  startWith,
  Subject,
  subscribeOn,
  Subscription,
  takeUntil,
} from 'rxjs';
import {
  MapFilterItem,
  MapFilterType,
} from 'app/shared/components/main-map/components/search-results/map-filter-state.model';
import { numericFilterOperatorsList } from 'app/shared/constants/filter-operators.constant';
import { FilterOperatorType } from 'app/shared/models/filter-operator-type.model';
import { FormatterUtils } from 'app/shared/utils/formatter.utils';
import { ObjectUtils } from 'app/shared/utils/object.utils';
import { MapFilterManager } from '../../map-filter.manager';

interface InputValueWithOperatorForm {
  operator: FormControl<FilterOperatorType>;
  value: FormControl<number>;
}

interface AttributesForm {
  baseinstalacao: FormControl<string>;
  tracado: FormControl<string>;
  afetacaoprincipal: FormControl<string>;
  diametro: FormGroup<InputValueWithOperatorForm>;
  comprimento: FormGroup<InputValueWithOperatorForm>;
  largura: FormGroup<InputValueWithOperatorForm>;
  altura: FormGroup<InputValueWithOperatorForm>;
  cota: FormGroup<InputValueWithOperatorForm>;
  tipoutilizacao: FormControl<string>;
  estadooperacional: FormControl<string>;
  estadoocupacao: FormGroup<InputValueWithOperatorForm>;
  created_date: FormControl<Date>;
}

const fieldsWithOperator: Array<keyof AttributesForm> = [
  'diametro',
  'comprimento',
  'largura',
  'altura',
  'cota',
  'estadoocupacao',
];

const defaultValues: any = {};

fieldsWithOperator.forEach((field) => {
  defaultValues[field] = {
    operator: FilterOperatorType.Eq,
  };
});

type ItemHolder = {
  value: any;
  valueFormatted: string;
  operator: FilterOperatorType;
};

@Component({
  selector: 'app-attributes-filter-form',
  templateUrl: 'attributes-filter-form.component.html',
  styleUrls: ['attributes-filter-form.component.scss'],
})
export class AttributesFilterFormComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Input()
  fields: __esri.Field[] = [];

  formGroup: FormGroup<AttributesForm>;

  private _fieldsMapped = new Map<string, __esri.Field>();

  private domainListMapped = new Map<string, __esri.CodedValue[]>();

  operatorList = numericFilterOperatorsList;

  private filterList: MapFilterItem[] = [];
  private newFiltersMap = new Map<string, ItemHolder>();
  private filterKeySetRemoved = new Set<string>();

  private filtersSubscription: Subscription;

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mapFilterManager: MapFilterManager
  ) {}

  ngOnInit(): void {
    const createInputValueWithOperatorFormGroup = () => {
      return this.formBuilder.group<InputValueWithOperatorForm>({
        operator: this.formBuilder.control<FilterOperatorType>(null),
        value: this.formBuilder.control<number>(null),
      });
    };

    this.formGroup = this.formBuilder.group({
      baseinstalacao: this.formBuilder.control<string>(null),
      tracado: this.formBuilder.control<string>(null),
      afetacaoprincipal: this.formBuilder.control<string>(null),
      diametro: createInputValueWithOperatorFormGroup(),
      comprimento: createInputValueWithOperatorFormGroup(),
      largura: createInputValueWithOperatorFormGroup(),
      altura: createInputValueWithOperatorFormGroup(),
      cota: createInputValueWithOperatorFormGroup(),
      tipoutilizacao: this.formBuilder.control<string>(null),
      estadooperacional: this.formBuilder.control<string>(null),
      estadoocupacao: createInputValueWithOperatorFormGroup(),
      created_date: this.formBuilder.control<Date>(null),
    });

    const fieldsWithDomain: Array<keyof AttributesForm> = [
      'baseinstalacao',
      'tracado',
      'afetacaoprincipal',
      'estadooperacional',
    ];

    merge(
      this.observeFormValueRangeChanges(this.formGroup, [
        'baseinstalacao',
        'tracado',
        'afetacaoprincipal',
        'tipoutilizacao',
        'estadooperacional',
        'created_date',
      ]).pipe(
        map((e) => {
          return { operator: FilterOperatorType.Eq, ...e };
        })
      ),
      ...fieldsWithOperator.map((fieldName) => {
        const formGroup: FormGroup<InputValueWithOperatorForm> =
          this.formGroup.get(fieldName) as any;
        return this.observeFormValueRangeChanges<InputValueWithOperatorForm>(
          formGroup,
          ['operator', 'value']
        ).pipe(
          map(() => {
            const rawValue = formGroup.getRawValue();
            return {
              fieldName,
              ...rawValue,
            };
          })
        );
      })
    )
      .pipe(takeUntil(this.lifecycle))
      .subscribe(({ fieldName, value, operator }) => {
        if (value === null || value === undefined || value === '') {
          this.newFiltersMap.delete(fieldName);
          this.filterKeySetRemoved.add(fieldName);
        } else {
          let valueFormatted: string;

          if (fieldsWithDomain.includes(fieldName)) {
            valueFormatted =
              this.getFieldDomain(fieldName)?.find((x) => x.code === value)
                ?.name ?? String(value);
          } else if (value instanceof Date) {
            valueFormatted = FormatterUtils.formatDateTime(value);
          } else {
            valueFormatted = String(value);
          }

          this.newFiltersMap.set(fieldName, {
            valueFormatted,
            value,
            operator,
          });
          this.filterKeySetRemoved.delete(fieldName);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fields) {
      this.domainListMapped.clear();
      this._fieldsMapped.clear();
      this.fields?.forEach((field) => {
        this._fieldsMapped.set(field.name, field);
      });
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onShowingInternal() {
    this.filtersSubscription?.unsubscribe();
    this.filtersSubscription = this.mapFilterManager.onFiltersChanged
      .pipe(subscribeOn(asapScheduler), takeUntil(this.lifecycle))
      .subscribe((list) => {
        this.filterList = list.filter(
          (x) => x.type === MapFilterType.Attributes
        );

        this.handleValues();
      });
  }

  onHiddenInternal() {
    this.filtersSubscription?.unsubscribe();
    this.handleValues();
  }

  onSubmit(e) {
    e?.preventDefault();

    const type = MapFilterType.Attributes;
    const addsOrUpdates = Array.from(this.newFiltersMap, ([field, holder]) => {
      let item = this.mapFilterManager.getFilterByTypeAndField(type, field);
      if (!item) {
        item = new MapFilterItem({ type, field });
      }

      item.text = holder.valueFormatted;
      item.valueAsStr = MapFilterItem.stringfyValue(holder.value);
      item.valueType = MapFilterItem.getValueType(holder.value);
      item.operator = holder.operator;

      return item;
    });
    const deletes = Array.from(this.filterKeySetRemoved.values(), (field) => {
      return this.mapFilterManager.getFilterKeyBy({ type, field });
    }).filter((x) => !!x);

    this.mapFilterManager.applyEditsFilters({
      adds: addsOrUpdates,
      updates: addsOrUpdates,
      deletes,
    });

    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onBtnClearClick() {
    this.resetValues();
  }

  getFieldDomain(fieldName: string) {
    let values = this.domainListMapped.get(fieldName);
    if (!values) {
      values = (
        this.fields?.find((x) => x.name === fieldName)
          ?.domain as __esri.CodedValueDomain
      )?.codedValues;

      if (fieldName === 'estadooperacional') {
        values = values?.filter((x) => x.code != 5);
      }

      this.domainListMapped.set(fieldName, values);
    }

    return values;
  }

  private handleValues() {
    this.filterKeySetRemoved.clear();
    this.newFiltersMap.clear();

    let values = this.filterList.reduce((acc, x) => {
      if (fieldsWithOperator.includes(x.field as any)) {
        acc[x.field] = {
          value: x.value,
          operator: x.operator,
        };
      } else {
        acc[x.field] = x.value;
      }
      return acc;
    }, {});

    const resetValues = ObjectUtils.merge({}, defaultValues, values);

    this.formGroup.reset(resetValues);
  }

  private resetValues() {
    this.filterKeySetRemoved.clear();
    this.newFiltersMap.clear();
    this.formGroup.reset(defaultValues);
  }

  private observeFormValueRangeChanges<T>(
    formGroup: FormGroup<any>,
    fieldNames: Array<keyof T>
  ) {
    return merge(
      ...fieldNames.map((fieldName) => {
        return this.observeFormValueChanges(formGroup, fieldName).pipe(
          map((value) => {
            return { fieldName, value };
          })
        );
      })
    );
  }

  private observeFormValueChanges<T>(
    formGroup: FormGroup<any>,
    fieldName: keyof T
  ) {
    const control = formGroup.get(fieldName as any);
    const initialValue = control.value;
    return control.valueChanges.pipe(
      startWith(initialValue),
      distinctUntilChanged()
    );
  }
}
